import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';

import { DiffCell, countDiff, diffLines } from '../../../utils/lineDiff';

interface PromptDiffViewProps {
  beforeTitle: string;
  afterTitle: string;
  before: string;
  after: string;
}

const CELL_STYLE: Record<DiffCell['type'], string> = {
  same: 'text-slate-700 dark:text-slate-300',
  removed: 'bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200',
  added: 'bg-green-100 text-green-900 dark:bg-green-950/60 dark:text-green-200',
};

const MARKER: Record<DiffCell['type'], string> = { same: ' ', removed: '-', added: '+' };

const Cell: React.FC<{ cell: DiffCell | null }> = ({ cell }) => (
  <Box
    className={`min-w-0 whitespace-pre-wrap break-words px-2 font-mono text-xs leading-5 ${
      cell ? CELL_STYLE[cell.type] : 'bg-slate-100 dark:bg-slate-800/60'
    }`}
  >
    {cell ? `${MARKER[cell.type]} ${cell.text}` : ' '}
  </Box>
);

export const PromptDiffView: React.FC<PromptDiffViewProps> = ({
  beforeTitle,
  afterTitle,
  before,
  after,
}) => {
  const rows = useMemo(() => diffLines(before, after), [before, after]);
  const { removed, added } = useMemo(() => countDiff(rows), [rows]);

  return (
    <Box className="min-w-0 rounded-lg border border-slate-200 dark:border-slate-700">
      <Box className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3 dark:border-slate-700">
        <Typography variant="subtitle2" className="text-gray-900 dark:text-slate-100">
          Changes
        </Typography>
        <Chip label={`-${removed}`} size="small" color="error" variant="outlined" />
        <Chip label={`+${added}`} size="small" color="success" variant="outlined" />
        {removed === 0 && added === 0 && (
          <Typography variant="caption" className="text-slate-500 dark:text-slate-400">
            Identical content
          </Typography>
        )}
      </Box>
      <Box className="max-h-[50vh] overflow-auto">
        <Box className="sticky top-0 z-10 grid grid-cols-2 gap-px bg-slate-200 text-xs font-semibold dark:bg-slate-700">
          <Box className="bg-white px-2 py-1 dark:bg-slate-900">{beforeTitle}</Box>
          <Box className="bg-white px-2 py-1 dark:bg-slate-900">{afterTitle}</Box>
        </Box>
        <Box className="grid grid-cols-2 gap-px bg-slate-200 dark:bg-slate-700">
          {rows.map((row, index) => (
            <React.Fragment key={index}>
              <Cell cell={row.left} />
              <Cell cell={row.right} />
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
